import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, RotateCcw, CheckCircle, XCircle, BookOpen, Star } from 'lucide-react';
import { useCourse } from '../hooks/useCourse';
import { useAuth } from '../hooks/useAuth';
import { useProgress } from '../hooks/useProgress';
import { calculateScore } from '../lib/scoring';
import { saveAiFeedback, supabase } from '../lib/supabase';
import { callClaude } from '../lib/claude';
import { QuizQuestion, WrittenQuestion, Flashcard, MindmapData } from '../types';
import { LoadingSpinner } from './LoadingSpinner';
import { getCourseProgress, getNextRecommendation, Recommendation } from '../lib/recommendations';
import { RecommendationCard } from './RecommendationCard';
// EU-Geographie specific data (only used for that course)
import { writtenQuestions as euWrittenQuestions } from '../data/eu-geographie/written-questions';
import { flashcards as euFlashcards } from '../data/eu-geographie/flashcards';
import { mindmaps as euMindmaps } from '../data/eu-geographie/mindmaps';

const MC_QUIZ_LENGTH = 15;
const WRITTEN_QUIZ_LENGTH = 5;

type QuizMode = 'all' | 'pflicht';

// MC question with shuffled options
interface ShuffledMCQuestion extends QuizQuestion {
  shuffledOptions: string[];
  shuffledCorrectIndex: number;
}

type QuizItem =
  | { type: 'mc'; data: ShuffledMCQuestion }
  | { type: 'written'; data: WrittenQuestion };

// Track wrong question details for result display
interface WrongQuestionDetail {
  id: string;
  question: string;
  correctAnswer: string;
  explanation?: string;
  type: 'mc' | 'written';
}

interface ParsedFeedback {
  PUNKTE?: string;
  RICHTIG?: string;
  FEHLT?: string;
  AUSDRUCK?: string;
  GESAMT?: string;
}

function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// Smart question selection with wrong-answer priority AND rotation
function selectSmartQuestions<T extends { id: string }>(
  questions: T[],
  wrongHistory: string[],
  lastQuizQuestionIds: string[],
  count: number
): T[] {
  // Step 1: Calculate weights for each question
  const questionWeights = questions.map(q => {
    const wrongCount = wrongHistory.filter(id => id === q.id).length;
    const wasInLastQuiz = lastQuizQuestionIds.includes(q.id);

    let weight = 1; // Base weight

    // Often wrong → higher chance
    if (wrongCount >= 3) weight = 4;
    else if (wrongCount >= 2) weight = 3;
    else if (wrongCount === 1) weight = 2;

    // Was in last quiz → LOWER chance (rotation!)
    if (wasInLastQuiz) weight = Math.max(0.5, weight - 2);

    // Never wrong AND was in last quiz → very low
    if (wrongCount === 0 && wasInLastQuiz) weight = 0.3;

    return { question: q, weight };
  });

  // Step 2: Weighted sampling without replacement
  const selected: T[] = [];
  const remaining = [...questionWeights];

  while (selected.length < count && remaining.length > 0) {
    // Calculate total weight
    const totalWeight = remaining.reduce((sum, qw) => sum + qw.weight, 0);

    // Select randomly based on weight
    let random = Math.random() * totalWeight;
    let selectedIndex = 0;

    for (let i = 0; i < remaining.length; i++) {
      random -= remaining[i].weight;
      if (random <= 0) {
        selectedIndex = i;
        break;
      }
    }

    selected.push(remaining[selectedIndex].question);
    remaining.splice(selectedIndex, 1);
  }

  // Step 3: Final shuffle for random order
  return shuffleArray(selected);
}

// Parse Claude AI response
function parseResponse(text: string): ParsedFeedback {
  const sections: ParsedFeedback = {};
  const labels: (keyof ParsedFeedback)[] = ['PUNKTE', 'RICHTIG', 'FEHLT', 'AUSDRUCK', 'GESAMT'];

  for (let i = 0; i < labels.length; i++) {
    const label = labels[i];
    const nextLabels = labels.slice(i + 1);
    const lookahead = nextLabels.length > 0
      ? `(?=[-•*]*\\s*\\**(?:${nextLabels.join('|')})\\**:)`
      : '';
    const regex = new RegExp(
      `[-•*]*\\s*\\**${label}\\**:?\\s*(.+?)${lookahead || '(?=$)'}`,
      'si'
    );
    const match = text.match(regex);
    if (match && match[1]) {
      sections[label] = match[1].trim();
    }
  }

  return sections;
}

// Lernhilfe mapping
interface LernhilfeData {
  title: string;
  mindmapId?: string;
  audioFiles: string[];
  audioTitle: string;
  flashcardCategory: string;
  summary: string;
}

// Lernhilfe Modal Component
interface LernhilfeModalProps {
  lernhilfe: LernhilfeData;
  lernhilfeTab: 'text' | 'audio' | 'karten' | 'mindmap';
  setLernhilfeTab: (tab: 'text' | 'audio' | 'karten' | 'mindmap') => void;
  lernhilfeAudioPlaying: boolean;
  setLernhilfeAudioPlaying: (playing: boolean) => void;
  currentAudioIndex: number;
  setCurrentAudioIndex: (index: number) => void;
  lernhilfeAudioRef: React.RefObject<HTMLAudioElement | null>;
  onClose: () => void;
  flashcards: Flashcard[];
  mindmaps: MindmapData[];
}

function LernhilfeModal({
  lernhilfe,
  lernhilfeTab,
  setLernhilfeTab,
  lernhilfeAudioPlaying,
  setLernhilfeAudioPlaying,
  currentAudioIndex,
  setCurrentAudioIndex,
  lernhilfeAudioRef,
  onClose,
  flashcards,
  mindmaps,
}: LernhilfeModalProps) {
  // Get flashcards for this category
  const categoryFlashcards = flashcards.filter(
    fc => fc.category === lernhilfe.flashcardCategory
  );

  // Get mindmap for this category
  const categoryMindmap = lernhilfe.mindmapId
    ? mindmaps.find(mm => mm.id === lernhilfe.mindmapId)
    : undefined;

  const toggleAudio = () => {
    if (!lernhilfeAudioRef.current) return;
    if (lernhilfeAudioPlaying) {
      lernhilfeAudioRef.current.pause();
      setLernhilfeAudioPlaying(false);
    } else {
      lernhilfeAudioRef.current.play();
      setLernhilfeAudioPlaying(true);
    }
  };

  const nextAudio = () => {
    if (currentAudioIndex < lernhilfe.audioFiles.length - 1) {
      setCurrentAudioIndex(currentAudioIndex + 1);
      setLernhilfeAudioPlaying(false);
    }
  };

  const prevAudio = () => {
    if (currentAudioIndex > 0) {
      setCurrentAudioIndex(currentAudioIndex - 1);
      setLernhilfeAudioPlaying(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.85)',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
      padding: '16px',
      overflowY: 'auto',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
      }}>
        <h2 style={{ color: '#ff9800', fontSize: '18px', fontWeight: 700, margin: 0 }}>
          💡 {lernhilfe.title}
        </h2>
        <button
          onClick={onClose}
          style={{
            background: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            color: '#fff',
            fontSize: '18px',
            cursor: 'pointer',
          }}
        >
          ✕
        </button>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '16px',
        flexWrap: 'wrap',
      }}>
        {['text', 'audio', 'karten', 'mindmap'].map(tab => {
          // Skip mindmap tab if no mindmap available
          if (tab === 'mindmap' && !categoryMindmap) return null;

          const isActive = lernhilfeTab === tab;
          const icons: Record<string, string> = {
            text: '📝',
            audio: '🔊',
            karten: '📚',
            mindmap: '🗺️',
          };
          const labels: Record<string, string> = {
            text: 'Text',
            audio: 'Audio',
            karten: 'Karten',
            mindmap: 'Mindmap',
          };

          return (
            <button
              key={tab}
              onClick={() => setLernhilfeTab(tab as typeof lernhilfeTab)}
              style={{
                flex: '1 1 auto',
                minWidth: '70px',
                padding: '10px 12px',
                background: isActive ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255,255,255,0.08)',
                border: isActive ? '2px solid #ff9800' : '2px solid transparent',
                borderRadius: '10px',
                color: isActive ? '#ff9800' : 'rgba(255,255,255,0.7)',
                fontSize: '13px',
                fontWeight: isActive ? 700 : 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
              }}
            >
              {icons[tab]} {labels[tab]}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div style={{
        flex: 1,
        background: 'rgba(255,255,255,0.05)',
        borderRadius: '16px',
        padding: '16px',
        overflowY: 'auto',
      }}>
        {/* Text Tab */}
        {lernhilfeTab === 'text' && (
          <div style={{ color: '#e8eaf6', fontSize: '15px', lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>
            {lernhilfe.summary}
          </div>
        )}

        {/* Audio Tab */}
        {lernhilfeTab === 'audio' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#ce93d8', fontSize: '14px', marginBottom: '12px' }}>
              {lernhilfe.audioTitle}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginBottom: '16px' }}>
              Teil {currentAudioIndex + 1} von {lernhilfe.audioFiles.length}
            </div>

            <audio
              ref={lernhilfeAudioRef}
              src={lernhilfe.audioFiles[currentAudioIndex]}
              onEnded={() => {
                setLernhilfeAudioPlaying(false);
                if (currentAudioIndex < lernhilfe.audioFiles.length - 1) {
                  setCurrentAudioIndex(currentAudioIndex + 1);
                }
              }}
              onPlay={() => setLernhilfeAudioPlaying(true)}
              onPause={() => setLernhilfeAudioPlaying(false)}
            />

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginBottom: '16px' }}>
              <button
                onClick={prevAudio}
                disabled={currentAudioIndex === 0}
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  background: currentAudioIndex > 0 ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.05)',
                  border: 'none',
                  color: currentAudioIndex > 0 ? '#fff' : 'rgba(255,255,255,0.3)',
                  fontSize: '18px',
                  cursor: currentAudioIndex > 0 ? 'pointer' : 'not-allowed',
                }}
              >
                ⏮
              </button>
              <button
                onClick={toggleAudio}
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: '#ff9800',
                  border: 'none',
                  color: '#1a1a2e',
                  fontSize: '24px',
                  cursor: 'pointer',
                }}
              >
                {lernhilfeAudioPlaying ? '⏸' : '▶️'}
              </button>
              <button
                onClick={nextAudio}
                disabled={currentAudioIndex >= lernhilfe.audioFiles.length - 1}
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  background: currentAudioIndex < lernhilfe.audioFiles.length - 1 ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.05)',
                  border: 'none',
                  color: currentAudioIndex < lernhilfe.audioFiles.length - 1 ? '#fff' : 'rgba(255,255,255,0.3)',
                  fontSize: '18px',
                  cursor: currentAudioIndex < lernhilfe.audioFiles.length - 1 ? 'pointer' : 'not-allowed',
                }}
              >
                ⏭
              </button>
            </div>
          </div>
        )}

        {/* Karten Tab */}
        {lernhilfeTab === 'karten' && (
          <div>
            {categoryFlashcards.length === 0 ? (
              <div style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', padding: '20px' }}>
                Keine Lernkarten für diese Kategorie
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {categoryFlashcards.slice(0, 5).map((card, idx) => (
                  <div key={card.id} style={{
                    background: 'rgba(255,255,255,0.08)',
                    borderRadius: '12px',
                    padding: '14px',
                  }}>
                    <div style={{ color: '#ffd54f', fontSize: '13px', fontWeight: 700, marginBottom: '6px' }}>
                      {idx + 1}. {card.question}
                    </div>
                    <div style={{ color: '#e8eaf6', fontSize: '14px', lineHeight: '1.5' }}>
                      {card.answer}
                    </div>
                  </div>
                ))}
                {categoryFlashcards.length > 5 && (
                  <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', textAlign: 'center' }}>
                    +{categoryFlashcards.length - 5} weitere Karten im Lernkarten-Bereich
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Mindmap Tab */}
        {lernhilfeTab === 'mindmap' && categoryMindmap && (
          <div>
            <h3 style={{ color: '#ffd54f', fontSize: '16px', marginBottom: '12px' }}>
              {categoryMindmap.title}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {categoryMindmap.cards.map((card, idx) => (
                <div key={idx}>
                  <div style={{
                    color: card.color || '#ff9800',
                    fontWeight: 700,
                    fontSize: '15px',
                    marginBottom: '8px',
                  }}>
                    • {card.title}
                  </div>
                  <div style={{ paddingLeft: '16px' }}>
                    {card.items.map((item, itemIdx) => (
                      <div key={itemIdx} style={{
                        color: '#e8eaf6',
                        fontSize: '14px',
                        lineHeight: '1.6',
                        marginBottom: '4px',
                      }}>
                        → {item}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Close button at bottom */}
      <button
        onClick={onClose}
        style={{
          marginTop: '16px',
          padding: '14px',
          background: 'rgba(255, 152, 0, 0.15)',
          border: '2px solid rgba(255, 152, 0, 0.4)',
          borderRadius: '12px',
          color: '#ff9800',
          fontSize: '15px',
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        Verstanden, weiter lernen!
      </button>
    </div>
  );
}

const categoryLernhilfe: Record<string, LernhilfeData> = {
  grundsaetze: {
    title: 'Grundsätze der EU',
    mindmapId: 'mm-grundsaetze',
    audioFiles: ['/audio/grundsatz1.mp3', '/audio/grundsatz2.mp3', '/audio/grundsatz3.mp3', '/audio/grundsatz4.mp3'],
    audioTitle: 'Lektion 1: Grundsätze',
    flashcardCategory: 'grundsaetze',
    summary: 'Die 4 Grundsätze der EU sind:\n1. Gemeinsame Werte und Ziele (Freiheit, Demokratie, Menschenrechte)\n2. Wirtschaftliche Zusammenarbeit (freier Handel, Binnenmarkt)\n3. Politische Zusammenarbeit (EU-Parlament, gemeinsame Gesetze)\n4. Zusammenhalt in Krisenzeiten (gegenseitige Hilfe)',
  },
  vorteile: {
    title: 'Vorteile der EU',
    mindmapId: 'mm-vorteile-nachteile',
    audioFiles: ['/audio/vorteil1.mp3', '/audio/vorteil2.mp3', '/audio/vorteil3.mp3', '/audio/vorteil4.mp3', '/audio/vorteil5.mp3'],
    audioTitle: 'Lektion 2: Vorteile',
    flashcardCategory: 'vorteile',
    summary: 'Die 5 Vorteile der EU:\n1. Freies Reisen und Arbeiten (ohne Visum)\n2. Gemeinsame Währung Euro (20 Länder)\n3. Frieden und Stabilität\n4. Binnenmarkt (freier Handel ohne Zölle)\n5. Menschenrechte (hohe Standards)',
  },
  nachteile: {
    title: 'Nachteile der EU',
    mindmapId: 'mm-vorteile-nachteile',
    audioFiles: ['/audio/nachteil1.mp3', '/audio/nachteil2.mp3', '/audio/nachteil3.mp3'],
    audioTitle: 'Lektion 2: Nachteile',
    flashcardCategory: 'nachteile',
    summary: 'Die 3 Nachteile der EU:\n1. Bürokratie (komplexe Strukturen, langsame Entscheidungen)\n2. Souveränitätsverlust (Länder geben Macht an EU ab, z.B. Brexit)\n3. Ungleiche wirtschaftliche Entwicklung (manche Länder reicher als andere)',
  },
  aufgaben: {
    title: 'EU Aufgaben & Geschichte',
    audioFiles: ['/audio/grundsatz1.mp3'],
    audioTitle: 'Lektion 1: EU Geschichte',
    flashcardCategory: 'aufgaben',
    summary: 'Wichtige Daten:\n- 1951: EGKS gegründet (Kohle & Stahl, 6 Gründerländer)\n- 1957: Römische Verträge → EWG + Euratom\n- 1993: EU gegründet durch Vertrag von Maastricht\n\nGründerländer: Belgien, Deutschland, Frankreich, Italien, Luxemburg, Niederlande',
  },
  geschichte: {
    title: 'EU Geschichte',
    audioFiles: ['/audio/grundsatz1.mp3'],
    audioTitle: 'Lektion 1: Geschichte',
    flashcardCategory: 'aufgaben',
    summary: 'EGKS 1951: Damit Kohle und Stahl nicht mehr für Kriege verwendet werden.\n6 Gründerländer: BE, DE, FR, IT, LU, NL\n1957: EWG (gemeinsamer Markt) + Euratom\n1993: EU durch Vertrag von Maastricht',
  },
  mitglieder: {
    title: 'EU Mitgliedsstaaten',
    mindmapId: 'mm-mitglieder',
    audioFiles: ['/audio/lektion3.mp3', '/audio/lektion4.mp3', '/audio/lektion5.mp3', '/audio/lektion6.mp3', '/audio/lektion7.mp3', '/audio/lektion8.mp3'],
    audioTitle: 'Lektionen 3-8: Regionen',
    flashcardCategory: 'mitglieder',
    summary: '27 EU-Mitgliedsstaaten in 6 Regionen:\n- Mitteleuropa (8): DE, AT, PL, CZ, SK, HU, SI, HR\n- Westeuropa (5): FR, BE, NL, LU, IE\n- Nordeuropa (3): SE, FI, DK\n- Südeuropa (6): IT, ES, PT, GR, MT, CY\n- Baltikum (3): EE, LV, LT\n- Südosteuropa (2): RO, BG',
  },
  regionen: {
    title: 'EU Regionen',
    mindmapId: 'mm-mitglieder',
    audioFiles: ['/audio/lektion3.mp3'],
    audioTitle: 'Lektion 3: Regionen',
    flashcardCategory: 'laender',
    summary: '6 EU-Regionen:\n1. Mitteleuropa (8 Länder, z.B. Österreich)\n2. Westeuropa (5, z.B. Frankreich)\n3. Nordeuropa (3, z.B. Schweden)\n4. Südeuropa (6, z.B. Italien)\n5. Baltikum (3, z.B. Estland)\n6. Südosteuropa (2: Rumänien, Bulgarien)',
  },
  abkuerzungen: {
    title: 'EU Abkürzungen',
    audioFiles: ['/audio/grundsatz1.mp3'],
    audioTitle: 'Lektion 1',
    flashcardCategory: 'abkuerzungen',
    summary: 'EU = Europäische Union (gegründet 1993)\nEGKS = Europäische Gemeinschaft für Kohle und Stahl (1951)\nEWG = Europäische Wirtschaftsgemeinschaft (1957)\nEuratom = Europäische Atomgemeinschaft (1957)',
  },
  laender: {
    title: 'EU Länder',
    audioFiles: ['/audio/lektion3.mp3'],
    audioTitle: 'Lektion 3: Länder',
    flashcardCategory: 'laender',
    summary: '27 EU-Länder in 6 Regionen.\nJedes Land hat Hauptstadt, Flagge und besondere Merkmale.\nÖsterreich ist in Mitteleuropa.',
  },
};

// Wrong Questions Display Component for Results
function WrongQuestionsSection({ wrongQuestions }: { wrongQuestions: WrongQuestionDetail[] }) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (wrongQuestions.length === 0) return null;

  return (
    <div style={{
      background: 'rgba(239, 83, 80, 0.08)',
      border: '1px solid rgba(239, 83, 80, 0.2)',
      borderRadius: '16px',
      padding: '16px',
      marginBottom: '20px',
      marginTop: '16px',
    }}>
      {/* Header with toggle */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}>
          <span style={{ fontSize: '20px' }}>❌</span>
          <span style={{ color: '#ef5350', fontWeight: 700, fontSize: '15px' }}>
            {wrongQuestions.length} {wrongQuestions.length === 1 ? 'Frage' : 'Fragen'} falsch
          </span>
        </div>
        <span style={{
          color: 'rgba(255,255,255,0.5)',
          fontSize: '20px',
          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease',
        }}>
          ▼
        </span>
      </button>

      {/* Smart repetition hint */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginTop: '12px',
        padding: '10px 12px',
        background: 'rgba(255, 152, 0, 0.1)',
        borderRadius: '10px',
      }}>
        <span style={{ fontSize: '16px' }}>💪</span>
        <span style={{
          color: '#ff9800',
          fontSize: '13px',
          lineHeight: '1.4',
        }}>
          Diese Fragen kommen beim nächsten Mal häufiger dran, damit du sie üben kannst!
        </span>
      </div>

      {/* Expandable list of wrong questions */}
      {isExpanded && (
        <div style={{
          marginTop: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}>
          {wrongQuestions.map((wq, index) => (
            <div
              key={wq.id}
              style={{
                background: 'rgba(255, 255, 255, 0.03)',
                borderRadius: '12px',
                padding: '14px',
                borderLeft: '3px solid #ef5350',
              }}
            >
              {/* Question */}
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
                marginBottom: '10px',
              }}>
                <span style={{ color: '#ef5350', fontSize: '14px' }}>❌</span>
                <div style={{
                  color: '#e8eaf6',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  flex: 1,
                }}>
                  <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>Frage: </span>
                  {wq.question}
                </div>
              </div>

              {/* Correct answer */}
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
                marginBottom: wq.explanation ? '10px' : 0,
              }}>
                <span style={{ color: '#4caf50', fontSize: '14px' }}>✅</span>
                <div style={{
                  color: '#4caf50',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  flex: 1,
                }}>
                  <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>Richtige Antwort: </span>
                  {wq.correctAnswer}
                </div>
              </div>

              {/* Explanation (if available) */}
              {wq.explanation && (
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                }}>
                  <span style={{ color: '#42a5f5', fontSize: '14px' }}>📝</span>
                  <div style={{
                    color: 'rgba(255,255,255,0.7)',
                    fontSize: '13px',
                    lineHeight: '1.5',
                    flex: 1,
                    fontStyle: 'italic',
                  }}>
                    {wq.explanation}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function Quiz() {
  const { courseId } = useParams<{ courseId: string }>();
  const [searchParams] = useSearchParams();
  const categoryFilter = searchParams.get('category');
  const { course, isLoading } = useCourse(courseId || '');
  const { user } = useAuth();
  const { submitQuizResult } = useProgress(user?.id, courseId || '');

  // Get course-specific data (only EU-Geographie has written questions mixed into quiz)
  const isEuCourse = courseId === 'eu-geographie';
  const writtenQuestions: WrittenQuestion[] = isEuCourse ? euWrittenQuestions : [];
  const flashcards: Flashcard[] = isEuCourse ? euFlashcards : (course?.flashcards || []);
  const mindmaps: MindmapData[] = isEuCourse ? euMindmaps : (course?.mindmaps || []);

  // Mode selection
  const [quizMode, setQuizMode] = useState<QuizMode | null>(null);

  const [quizKey, setQuizKey] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongQuestions, setWrongQuestions] = useState<string[]>([]);
  const [wrongQuestionDetails, setWrongQuestionDetails] = useState<WrongQuestionDetail[]>([]);
  const [isFinished, setIsFinished] = useState(false);
  const [startTime, setStartTime] = useState(Date.now());

  // Written question states
  const [writtenAnswer, setWrittenAnswer] = useState('');
  const [writtenFeedback, setWrittenFeedback] = useState<ParsedFeedback | null>(null);
  const [writtenRawResponse, setWrittenRawResponse] = useState('');
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [isSavingResults, setIsSavingResults] = useState(false);

  // TEIL 1: Differenzierte Punkteerfassung
  const [mcCorrect, setMcCorrect] = useState(0);
  const [mcTotal, setMcTotal] = useState(0);
  const [writtenPointsEarned, setWrittenPointsEarned] = useState(0);
  const [writtenPointsMax, setWrittenPointsMax] = useState(0);

  // TEIL 2: Lernhilfe Popup
  const [showLernhilfe, setShowLernhilfe] = useState(false);
  const [lernhilfeTab, setLernhilfeTab] = useState<'text' | 'audio' | 'karten' | 'mindmap'>('text');
  const [lernhilfeAudioPlaying, setLernhilfeAudioPlaying] = useState(false);
  const [currentAudioIndex, setCurrentAudioIndex] = useState(0);
  const lernhilfeAudioRef = useRef<HTMLAudioElement>(null);
  const [lastMcWasWrong, setLastMcWasWrong] = useState(false);
  const [lastWrittenNeedsHelp, setLastWrittenNeedsHelp] = useState(false);

  // KI-Nachfrage nach jeder Frage
  const [followUpQuestion, setFollowUpQuestion] = useState('');
  const [followUpAnswers, setFollowUpAnswers] = useState<Array<{ question: string; answer: string }>>([]);
  const [isAskingFollowUp, setIsAskingFollowUp] = useState(false);

  // Intelligente Fragenauswahl: Fehlerhistorie + Rotation
  const [wrongHistory, setWrongHistory] = useState<string[]>([]);
  const [lastQuizQuestionIds, setLastQuizQuestionIds] = useState<string[]>([]);

  // Recommendation for result screen
  const [recommendation, setRecommendation] = useState<Recommendation | null>(null);

  // Load quiz history from Supabase (wrong questions + last quiz IDs for rotation)
  useEffect(() => {
    async function loadQuizHistory() {
      if (!user?.id || !courseId) return;

      try {
        // Load last 5 quizzes for wrong question history (filtered by course!)
        const { data } = await supabase
          .from('quiz_results')
          .select('wrong_questions, details')
          .eq('user_id', user.id)
          .eq('course_id', courseId)
          .eq('mode', 'quiz')
          .order('created_at', { ascending: false })
          .limit(5);

        if (data && data.length > 0) {
          // All wrong questions from last 5 quizzes
          const allWrong = data.flatMap(r => (r.wrong_questions as string[]) || []);
          setWrongHistory(allWrong);

          // Last quiz's question IDs for rotation (from details or wrong_questions as fallback)
          const lastQuiz = data[0];
          const lastIds = (lastQuiz.details as { all_question_ids?: string[] })?.all_question_ids
            || (lastQuiz.wrong_questions as string[])
            || [];
          setLastQuizQuestionIds(lastIds);

          console.log('Quiz history loaded:', {
            wrongCount: allWrong.length,
            lastQuizIds: lastIds.length,
          });
        }
      } catch (error) {
        console.error('Error loading quiz history:', error);
      }
    }

    loadQuizHistory();
  }, [user?.id, courseId]);

  // Count questions by difficulty — filtered by category if active
  const questionCounts = useMemo(() => {
    if (!course) return { pflicht: 0, bonus: 0, total: 0, writtenPflicht: 0 };
    const mcPool = categoryFilter
      ? course.quizQuestions.filter(q => q.category === categoryFilter)
      : course.quizQuestions;
    const wPool = categoryFilter
      ? writtenQuestions.filter(q => q.category === categoryFilter)
      : writtenQuestions;
    const pflicht = mcPool.filter(q => q.difficulty === 'pflicht').length;
    const bonus = mcPool.filter(q => q.difficulty === 'bonus').length;
    const writtenPflicht = wPool.filter(q => q.difficulty === 'pflicht').length;
    return { pflicht, bonus, total: pflicht + bonus, writtenPflicht };
  }, [course, categoryFilter]);

  const questions = useMemo<QuizItem[]>(() => {
    if (!course || !quizMode) return [];

    let mcPool: QuizQuestion[];
    let writtenPool: WrittenQuestion[];

    if (quizMode === 'pflicht') {
      mcPool = course.quizQuestions.filter(q => q.difficulty === 'pflicht');
      writtenPool = writtenQuestions.filter(q => q.difficulty === 'pflicht');
    } else {
      mcPool = course.quizQuestions;
      writtenPool = writtenQuestions;
    }

    // Filter by category if specified
    if (categoryFilter) {
      mcPool = mcPool.filter(q => q.category === categoryFilter);
      writtenPool = writtenPool.filter(q => q.category === categoryFilter);
    }

    // Select MC questions with smart selection (wrong priority + rotation)
    // AND shuffle options for each question to prevent predictable answers
    const selectedMC: QuizItem[] = selectSmartQuestions(mcPool, wrongHistory, lastQuizQuestionIds, MC_QUIZ_LENGTH)
      .map(q => {
        // Create shuffled options with index tracking
        const correctAnswer = q.options[q.correctIndex];
        const shuffledOptions = shuffleArray([...q.options]);
        const shuffledCorrectIndex = shuffledOptions.indexOf(correctAnswer);

        const shuffledQuestion: ShuffledMCQuestion = {
          ...q,
          shuffledOptions,
          shuffledCorrectIndex,
        };
        return { type: 'mc' as const, data: shuffledQuestion };
      });

    // Select written questions with smart selection (wrong priority + rotation)
    const selectedWritten: QuizItem[] = selectSmartQuestions(writtenPool, wrongHistory, lastQuizQuestionIds, WRITTEN_QUIZ_LENGTH)
      .map(q => ({ type: 'written' as const, data: q }));

    // Mix everything together
    return shuffleArray([...selectedMC, ...selectedWritten]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course, quizMode, quizKey, wrongHistory, lastQuizQuestionIds]);

  // Submit written answer to Claude AI
  const submitWrittenAnswer = async () => {
    if (!writtenAnswer.trim()) return;
    const currentQuestion = questions[currentIndex];
    if (currentQuestion.type !== 'written') return;

    setIsEvaluating(true);
    const wq = currentQuestion.data as WrittenQuestion;

    try {
      const data = await callClaude({
        model: 'claude-sonnet-4-20250514',
        max_tokens: 500,
        system: `Du bist ein freundlicher Lern-Tutor für Adriana (14, 4. Klasse MS Wien).
Bewerte ihre Antwort auf die Frage. Gib Feedback in genau diesem Format:

PUNKTE: X von Y (wie viele Kernpunkte genannt)
RICHTIG: Was war korrekt
FEHLT: Was fehlt oder was falsch ist. Nenne kurz die korrekte Information.
AUSDRUCK: Kurzer Tipp zur Formulierung (nur wenn nötig, sonst "Kein Tipp nötig")
GESAMT: Ermutigendes Fazit (1 Satz)

Wichtig: Jede Zeile muss mit dem Label beginnen, gefolgt von Doppelpunkt und dem Inhalt.
Sei ermutigend aber ehrlich. Sprache: einfaches Deutsch.`,
        messages: [{
          role: 'user',
          content: `Frage: ${wq.question}\nErwartete Antwort: ${wq.expectedAnswer}\nMaximale Punkte: ${wq.maxPoints}\nAdrianas Antwort: ${writtenAnswer}\nBewerte ihre Antwort.`,
        }],
      }, 'quiz-check');


      const text = data.content?.[0]?.text || '';

      console.log('=== CLAUDE RAW RESPONSE ===');
      console.log(text);

      setWrittenRawResponse(text);
      const parsed = parseResponse(text);
      console.log('=== PARSED SECTIONS ===', parsed);
      setWrittenFeedback(parsed);

      // Save to Supabase
      if (user?.id) {
        await saveAiFeedback(user.id, 'written_quiz', text, parsed as Record<string, string>, {
          question: wq.question,
          userAnswer: writtenAnswer,
          scoreInfo: parsed.PUNKTE || '',
        });
      }

      // Track written points
      const punkteMatch = (parsed.PUNKTE || '').match(/(\d+)\s*von\s*(\d+)/);
      if (punkteMatch) {
        const erreicht = parseInt(punkteMatch[1]);
        const maximal = parseInt(punkteMatch[2]);
        setWrittenPointsEarned(prev => prev + erreicht);
        setWrittenPointsMax(prev => prev + maximal);

        // Set lastWrittenNeedsHelp if not max points
        setLastWrittenNeedsHelp(erreicht < maximal);

        // Count as "wrong" for wrongQuestions tracking if < 50%
        if (erreicht < maximal * 0.5) {
          setWrongQuestions(w => [...w, wq.id]);
          setWrongQuestionDetails(w => [...w, {
            id: wq.id,
            question: wq.question,
            correctAnswer: wq.expectedAnswer,
            explanation: undefined,
            type: 'written',
          }]);
        }
      } else {
        // Fallback if no points parsed - use maxPoints from question
        setWrittenPointsMax(prev => prev + wq.maxPoints);
        setLastWrittenNeedsHelp(true); // Assume needs help if no score parsed
      }
    } catch (error) {
      console.error('Claude API Error:', error);
      setWrittenFeedback({});
      setWrittenRawResponse('Fehler bei der KI-Bewertung. Bitte versuche es nochmal.');
    }

    setIsEvaluating(false);
    setIsAnswered(true);
  };

  // KI-Nachfrage stellen
  const askFollowUp = async () => {
    if (!followUpQuestion.trim()) return;
    setIsAskingFollowUp(true);

    const currentQ = questions[currentIndex];
    const questionText = currentQ.type === 'mc'
      ? (currentQ.data as QuizQuestion).question
      : (currentQ.data as WrittenQuestion).question;

    const correctAnswer = currentQ.type === 'mc'
      ? (currentQ.data as QuizQuestion).options[(currentQ.data as QuizQuestion).correctIndex]
      : (currentQ.data as WrittenQuestion).expectedAnswer;

    const explanation = currentQ.type === 'mc'
      ? (currentQ.data as QuizQuestion).explanation
      : '';

    try {
      const data = await callClaude({
        model: 'claude-sonnet-4-20250514',
        max_tokens: 500,
        system: `Du bist ein freundlicher Lern-Tutor für eine Schülerin (14 Jahre, 4. Klasse Mittelschule Wien).
Sie macht gerade ein Quiz zum Thema EU und hat eine Nachfrage zu einer Frage.

Kontext der Quiz-Frage: "${questionText}"
Richtige Antwort: "${correctAnswer}"
${explanation ? `Erklärung: "${explanation}"` : ''}

Antworte kurz und verständlich (max 3-4 Sätze).
Verwende einfaches Deutsch. Sei ermutigend.
Wenn sie etwas fragt das nichts mit dem Thema zu tun hat,
lenke sie freundlich zurück zum Lernstoff.`,
        messages: [
          ...followUpAnswers.flatMap(qa => [
            { role: 'user' as const, content: qa.question },
            { role: 'assistant' as const, content: qa.answer },
          ]),
          { role: 'user' as const, content: followUpQuestion },
        ],
      }, 'quiz-followup');

      const answer = data.content?.[0]?.text || 'Entschuldigung, ich konnte nicht antworten.';

      setFollowUpAnswers(prev => [...prev, {
        question: followUpQuestion,
        answer
      }]);
      setFollowUpQuestion('');

    } catch (error) {
      console.error('Follow-up API error:', error);
      setFollowUpAnswers(prev => [...prev, {
        question: followUpQuestion,
        answer: 'Fehler bei der Verbindung. Versuch es nochmal!'
      }]);
    }

    setIsAskingFollowUp(false);
  };

  if (isLoading || !course) {
    return <div className="loading">Laden...</div>;
  }

  // Show loading spinner during evaluation or saving
  if (isEvaluating) {
    return <LoadingSpinner text="KI bewertet..." />;
  }

  if (isSavingResults) {
    return <LoadingSpinner text="Ergebnis wird gespeichert..." />;
  }

  // Mode selection screen
  if (quizMode === null) {
    return (
      <div className="quiz-mode-select">
        <Link to={`/course/${courseId}`} className="back-link">
          <ArrowLeft size={18} />
          Zurück
        </Link>

        <h1>Quiz-Modus wählen</h1>
        <p className="mode-description">Wähle, welche Fragen du üben möchtest:</p>

        <div className="mode-cards">
          <button
            className="mode-card pflicht"
            onClick={() => {
              setQuizKey(k => k + 1); // Force new shuffle
              setQuizMode('pflicht');
              setStartTime(Date.now());
            }}
          >
            <div className="mode-icon">📋</div>
            <h2>Nur Pflicht</h2>
            <p>MC + Schriftliche Fragen gemischt</p>
            <span className="mode-count">{questionCounts.pflicht + questionCounts.writtenPflicht} Fragen</span>
            <span className="mode-badge recommended">Empfohlen für Test</span>
            <div style={{
              marginTop: '8px',
              fontSize: '12px',
              color: '#ce93d8',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}>
              ✍️ Enthält offene Fragen mit KI-Bewertung
            </div>
          </button>

          <button
            className="mode-card all"
            onClick={() => {
              setQuizKey(k => k + 1); // Force new shuffle
              setQuizMode('all');
              setStartTime(Date.now());
            }}
          >
            <div className="mode-icon">🎯</div>
            <h2>Alle Fragen</h2>
            <p>Pflicht + Bonus gemischt</p>
            <span className="mode-count">{questionCounts.total + writtenQuestions.length} Fragen</span>
            <span className="mode-badge">Mehr Übung</span>
            <div style={{
              marginTop: '8px',
              fontSize: '12px',
              color: '#ce93d8',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}>
              ✍️ Enthält offene Fragen mit KI-Bewertung
            </div>
          </button>
        </div>

        {/* Smart Selection Info Banner */}
        {wrongHistory.length > 0 && (
          <div style={{
            background: 'rgba(255, 152, 0, 0.1)',
            border: '1px solid rgba(255, 152, 0, 0.25)',
            borderRadius: '12px',
            padding: '14px 16px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
          }}>
            <span style={{ fontSize: '20px', flexShrink: 0, marginTop: '2px' }}>🎯</span>
            <div>
              <div style={{ color: '#ff9800', fontWeight: 700, fontSize: '14px', marginBottom: '4px' }}>
                Intelligente Auswahl aktiv
              </div>
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', lineHeight: 1.5 }}>
                <strong style={{ color: '#ff9800' }}>{new Set(wrongHistory).size} Fragen</strong> aus den letzten Runden waren falsch und kommen mit höherer Wahrscheinlichkeit wieder dran.
              </div>
            </div>
          </div>
        )}

        <div className="mode-info">
          <div className="info-item">
            <BookOpen size={16} />
            <span><strong>Pflicht:</strong> Direkt im Teststoff, musst du können!</span>
          </div>
          <div className="info-item">
            <Star size={16} />
            <span><strong>Bonus:</strong> Zusatzwissen für bessere Noten</span>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];

  const handleSelect = (index: number) => {
    if (isAnswered) return;
    setSelectedAnswer(index);
  };

  const handleSubmitMC = () => {
    if (selectedAnswer === null || currentQuestion.type !== 'mc') return;

    setIsAnswered(true);
    const mcQuestion = currentQuestion.data as ShuffledMCQuestion;
    setMcTotal(prev => prev + 1);

    // Use shuffledCorrectIndex for comparison
    if (selectedAnswer === mcQuestion.shuffledCorrectIndex) {
      setMcCorrect(prev => prev + 1);
      setCorrectCount((c) => c + 1);
      setLastMcWasWrong(false);
    } else {
      setWrongQuestions((w) => [...w, mcQuestion.id]);
      // Track full details for result display
      setWrongQuestionDetails((w) => [...w, {
        id: mcQuestion.id,
        question: mcQuestion.question,
        correctAnswer: mcQuestion.options[mcQuestion.correctIndex], // Original correct answer
        explanation: mcQuestion.explanation,
        type: 'mc',
      }]);
      setLastMcWasWrong(true);
    }
  };

  const handleNext = async () => {
    // Close Lernhilfe and stop audio
    setShowLernhilfe(false);
    if (lernhilfeAudioRef.current) {
      lernhilfeAudioRef.current.pause();
      setLernhilfeAudioPlaying(false);
    }
    setCurrentAudioIndex(0);
    setLastMcWasWrong(false);
    setLastWrittenNeedsHelp(false);
    // Reset follow-up states
    setFollowUpQuestion('');
    setFollowUpAnswers([]);
    setIsAskingFollowUp(false);

    if (currentIndex < questions.length - 1) {
      setCurrentIndex((i) => i + 1);
      setSelectedAnswer(null);
      setIsAnswered(false);
      // Reset written states
      setWrittenAnswer('');
      setWrittenFeedback(null);
      setWrittenRawResponse('');
    } else {
      // Show loading spinner while saving
      setIsSavingResults(true);

      // Calculate total score
      const totalEarned = mcCorrect + writtenPointsEarned;
      const totalMax = mcTotal + writtenPointsMax;
      const timeSeconds = Math.round((Date.now() - startTime) / 1000);

      // Collect all question IDs for rotation in next quiz
      const allQuestionIds = questions.map(q => q.data.id);

      await submitQuizResult('quiz', totalEarned, totalMax, wrongQuestions, timeSeconds, {
        mc_correct: mcCorrect,
        mc_total: mcTotal,
        written_earned: writtenPointsEarned,
        written_max: writtenPointsMax,
        all_question_ids: allQuestionIds,
      });

      // Invalidate Lernstand cache so it shows fresh data
      if (user?.id) {
        sessionStorage.removeItem(`lernstand-feedback-${user.id}`);

        // Load recommendation for result screen
        if (course) {
          const progress = await getCourseProgress(user.id, courseId || '', course);
          const totalPercentage = totalMax > 0 ? Math.round((totalEarned / totalMax) * 100) : 0;
          const rec = getNextRecommendation(courseId || '', progress, course, {
            justCompleted: 'quiz',
            lastScore: totalPercentage,
          });
          setRecommendation(rec);
        }
      }

      setIsSavingResults(false);
      setIsFinished(true);
    }
  };

  const restart = () => {
    setQuizKey((k) => k + 1);
    setCurrentIndex(0);
    setSelectedAnswer(null);
    setIsAnswered(false);
    setCorrectCount(0);
    setWrongQuestions([]);
    setWrongQuestionDetails([]);
    setIsFinished(false);
    setStartTime(Date.now());
    setWrittenAnswer('');
    setWrittenFeedback(null);
    setWrittenRawResponse('');
    // Reset new scoring states
    setMcCorrect(0);
    setMcTotal(0);
    setWrittenPointsEarned(0);
    setWrittenPointsMax(0);
    // Reset Lernhilfe states
    setShowLernhilfe(false);
    setLernhilfeTab('text');
    setLernhilfeAudioPlaying(false);
    setCurrentAudioIndex(0);
    setLastMcWasWrong(false);
    setLastWrittenNeedsHelp(false);
    // Reset follow-up states
    setFollowUpQuestion('');
    setFollowUpAnswers([]);
    setIsAskingFollowUp(false);
  };

  const backToModeSelect = () => {
    setQuizMode(null);
    restart();
  };

  if (isFinished) {
    // Calculate total percentage
    const totalEarned = mcCorrect + writtenPointsEarned;
    const totalMax = mcTotal + writtenPointsMax;
    const totalPercentage = totalMax > 0 ? Math.round((totalEarned / totalMax) * 100) : 0;
    const mcPercentage = mcTotal > 0 ? Math.round((mcCorrect / mcTotal) * 100) : 0;
    const writtenPercentage = writtenPointsMax > 0 ? Math.round((writtenPointsEarned / writtenPointsMax) * 100) : 0;

    const result = calculateScore(totalEarned, totalMax);

    return (
      <div className="quiz-result">
        <h1>{result.emoji} Quiz fertig!</h1>
        <div className="result-mode">
          {quizMode === 'pflicht' ? '📋 Nur Pflicht' : '🎯 Alle Fragen'}
        </div>

        {/* MC Ergebnis */}
        <div style={{
          background: 'rgba(66, 165, 245, 0.12)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '12px',
          marginTop: '16px',
        }}>
          <div style={{ color: '#42a5f5', fontWeight: 700, fontSize: '14px', marginBottom: '4px' }}>
            📋 Multiple Choice
          </div>
          <div style={{ color: '#e8eaf6', fontSize: '22px', fontWeight: 700 }}>
            {mcCorrect}/{mcTotal} richtig
            <span style={{ fontSize: '16px', color: 'rgba(255,255,255,0.5)', marginLeft: '8px' }}>
              ({mcPercentage}%)
            </span>
          </div>
        </div>

        {/* Schriftlich Ergebnis */}
        {writtenPointsMax > 0 && (
          <div style={{
            background: 'rgba(171, 71, 188, 0.12)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '12px',
          }}>
            <div style={{ color: '#ce93d8', fontWeight: 700, fontSize: '14px', marginBottom: '4px' }}>
              ✍️ Schriftliche Fragen
            </div>
            <div style={{ color: '#e8eaf6', fontSize: '22px', fontWeight: 700 }}>
              {writtenPointsEarned}/{writtenPointsMax} Punkte
              <span style={{ fontSize: '16px', color: 'rgba(255,255,255,0.5)', marginLeft: '8px' }}>
                ({writtenPercentage}%)
              </span>
            </div>
          </div>
        )}

        {/* Gesamt */}
        <div style={{
          background: 'rgba(255, 213, 79, 0.15)',
          border: '1px solid rgba(255, 213, 79, 0.3)',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '16px',
          textAlign: 'center',
        }}>
          <div style={{ color: '#ffd54f', fontWeight: 700, fontSize: '14px', marginBottom: '4px' }}>
            🏆 Gesamt
          </div>
          <div style={{ color: '#ffd54f', fontSize: '36px', fontWeight: 700 }}>
            {totalPercentage}%
          </div>
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginTop: '4px' }}>
            {totalEarned} von {totalMax} Punkten
          </div>
        </div>

        <p className="result-message">{result.message}</p>

        {/* Questions overview - what's still open */}
        {(() => {
          const filteredMC = categoryFilter
            ? course!.quizQuestions.filter(q => q.category === categoryFilter)
            : course!.quizQuestions;
          const filteredWritten = categoryFilter
            ? writtenQuestions.filter(q => q.category === categoryFilter)
            : writtenQuestions;
          const totalPool = quizMode === 'pflicht'
            ? filteredMC.filter(q => q.difficulty === 'pflicht').length + filteredWritten.filter(q => q.difficulty === 'pflicht').length
            : filteredMC.length + filteredWritten.length;
          const questionsInRound = questions.length;
          const remaining = totalPool - questionsInRound;
          const wrongCount = wrongQuestionDetails.length;

          return (
            <div style={{
              background: 'rgba(66, 165, 245, 0.08)',
              border: '1px solid rgba(66, 165, 245, 0.2)',
              borderRadius: '12px',
              padding: '14px 16px',
              marginBottom: '16px',
            }}>
              <div style={{ color: '#42a5f5', fontWeight: 700, fontSize: '14px', marginBottom: '8px' }}>
                📊 Übersicht
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>
                <div>Fragen in dieser Runde: <strong style={{ color: '#e8eaf6' }}>{questionsInRound}</strong></div>
                <div>Davon falsch: <strong style={{ color: wrongCount > 0 ? '#ef5350' : '#66bb6a' }}>{wrongCount}</strong></div>
                <div>Gesamt-Fragenpool: <strong style={{ color: '#e8eaf6' }}>{totalPool}</strong></div>
                {remaining > 0 && (
                  <div>Noch nicht geübt: <strong style={{ color: '#ff9800' }}>{remaining}</strong></div>
                )}
                {wrongCount > 0 && (
                  <div style={{ marginTop: '6px', color: '#ff9800', fontStyle: 'italic' }}>
                    → In der nächsten Runde kommen die {wrongCount} falschen Fragen bevorzugt wieder dran!
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Wrong Questions Section */}
        {wrongQuestionDetails.length > 0 && (
          <WrongQuestionsSection wrongQuestions={wrongQuestionDetails} />
        )}

        <button className="btn-primary" onClick={restart}>
          <RotateCcw size={18} />
          Nochmal ({quizMode === 'pflicht' ? 'Pflicht' : 'Alle'})
        </button>
        <button className="btn-secondary" onClick={backToModeSelect}>
          Anderen Modus wählen
        </button>
        <Link to={`/course/${courseId}`} className="btn-secondary">
          Zurück zum Kurs
        </Link>

        {/* Recommendation */}
        {recommendation && (
          <RecommendationCard recommendation={recommendation} />
        )}
      </div>
    );
  }

  // Render MC question
  if (currentQuestion.type === 'mc') {
    const mcQuestion = currentQuestion.data as ShuffledMCQuestion;
    return (
      <div className="quiz-view">
        <div className="quiz-header">
          <Link to={`/course/${courseId}`} className="back-link">
            <ArrowLeft size={18} />
            Zurück
          </Link>
          <span className="quiz-mode-badge">
            {quizMode === 'pflicht' ? '📋 Pflicht' : '🎯 Alle'}
          </span>
        </div>

        <div className="quiz-progress">
          Frage {currentIndex + 1} von {questions.length}
          {mcQuestion.difficulty === 'pflicht' && (
            <span className="difficulty-badge pflicht">Pflicht</span>
          )}
          {mcQuestion.difficulty === 'bonus' && (
            <span className="difficulty-badge bonus">Bonus</span>
          )}
        </div>

        <div className="quiz-question">
          <h2>{mcQuestion.question}</h2>
        </div>

        <div className="quiz-options">
          {mcQuestion.shuffledOptions.map((option, index) => {
            let className = 'quiz-option';
            if (isAnswered) {
              if (index === mcQuestion.shuffledCorrectIndex) {
                className += ' correct';
              } else if (index === selectedAnswer) {
                className += ' wrong';
              }
            } else if (index === selectedAnswer) {
              className += ' selected';
            }

            return (
              <button
                key={index}
                className={className}
                onClick={() => handleSelect(index)}
                disabled={isAnswered}
              >
                {option}
                {isAnswered && index === mcQuestion.shuffledCorrectIndex && (
                  <CheckCircle size={18} />
                )}
                {isAnswered && index === selectedAnswer && index !== mcQuestion.shuffledCorrectIndex && (
                  <XCircle size={18} />
                )}
              </button>
            );
          })}
        </div>

        {isAnswered && (
          <div className="quiz-explanation">
            <p>{mcQuestion.explanation}</p>
          </div>
        )}

        <div className="quiz-actions">
          {!isAnswered ? (
            <button
              className="btn-primary"
              onClick={handleSubmitMC}
              disabled={selectedAnswer === null}
            >
              Antworten
            </button>
          ) : (
            <>
              {/* Lernhilfe Button - nur bei falscher Antwort */}
              {lastMcWasWrong && categoryLernhilfe[mcQuestion.category] && (
                <button
                  onClick={() => {
                    setLernhilfeTab('text');
                    setShowLernhilfe(true);
                  }}
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: 'rgba(255, 152, 0, 0.15)',
                    border: '2px solid rgba(255, 152, 0, 0.4)',
                    borderRadius: '12px',
                    color: '#ff9800',
                    fontSize: '15px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  💡 Nachlernen: {categoryLernhilfe[mcQuestion.category].title}
                </button>
              )}

              {/* KI-Nachfrage Bereich */}
              <div style={{
                background: 'rgba(66, 165, 245, 0.08)',
                border: '1px solid rgba(66, 165, 245, 0.2)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '12px',
              }}>
                <div style={{
                  color: '#42a5f5',
                  fontSize: '14px',
                  fontWeight: 700,
                  marginBottom: '12px'
                }}>
                  🤖 Hast du noch eine Frage dazu?
                </div>

                {/* Bisherige Nachfragen anzeigen */}
                {followUpAnswers.map((qa, index) => (
                  <div key={index} style={{ marginBottom: '12px' }}>
                    <div style={{
                      background: 'rgba(255, 213, 79, 0.1)',
                      borderRadius: '10px',
                      padding: '10px 14px',
                      marginBottom: '6px',
                      fontSize: '14px',
                      color: '#ffd54f',
                    }}>
                      💬 {qa.question}
                    </div>
                    <div style={{
                      background: 'rgba(66, 165, 245, 0.1)',
                      borderRadius: '10px',
                      padding: '10px 14px',
                      fontSize: '14px',
                      color: '#e8eaf6',
                      lineHeight: '1.5',
                    }}>
                      🤖 {qa.answer}
                    </div>
                  </div>
                ))}

                {/* Eingabefeld */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    value={followUpQuestion}
                    onChange={(e) => setFollowUpQuestion(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && followUpQuestion.trim()) {
                        askFollowUp();
                      }
                    }}
                    placeholder="z.B. Was ist der Unterschied zwischen..."
                    style={{
                      flex: 1,
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: '10px',
                      color: '#e8eaf6',
                      fontSize: '14px',
                      padding: '12px',
                      fontFamily: 'Nunito, sans-serif',
                    }}
                  />
                  <button
                    onClick={askFollowUp}
                    disabled={!followUpQuestion.trim() || isAskingFollowUp}
                    style={{
                      background: followUpQuestion.trim() && !isAskingFollowUp ? '#42a5f5' : 'rgba(255,255,255,0.1)',
                      border: 'none',
                      borderRadius: '10px',
                      color: followUpQuestion.trim() && !isAskingFollowUp ? '#fff' : 'rgba(255,255,255,0.3)',
                      padding: '12px 16px',
                      fontSize: '14px',
                      fontWeight: 700,
                      cursor: followUpQuestion.trim() && !isAskingFollowUp ? 'pointer' : 'default',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {isAskingFollowUp ? '...' : '🤖'}
                  </button>
                </div>
              </div>

              <button className="btn-primary" onClick={handleNext}>
                {currentIndex < questions.length - 1 ? 'Weiter' : 'Ergebnis'}
              </button>
            </>
          )}
        </div>

        {/* Lernhilfe Modal */}
        {showLernhilfe && categoryLernhilfe[mcQuestion.category] && (
          <LernhilfeModal
            lernhilfe={categoryLernhilfe[mcQuestion.category]}
            lernhilfeTab={lernhilfeTab}
            setLernhilfeTab={setLernhilfeTab}
            lernhilfeAudioPlaying={lernhilfeAudioPlaying}
            setLernhilfeAudioPlaying={setLernhilfeAudioPlaying}
            currentAudioIndex={currentAudioIndex}
            setCurrentAudioIndex={setCurrentAudioIndex}
            lernhilfeAudioRef={lernhilfeAudioRef}
            onClose={() => {
              setShowLernhilfe(false);
              if (lernhilfeAudioRef.current) {
                lernhilfeAudioRef.current.pause();
                setLernhilfeAudioPlaying(false);
              }
            }}
            flashcards={flashcards}
            mindmaps={mindmaps}
          />
        )}
      </div>
    );
  }

  // Render written question
  const wQuestion = currentQuestion.data as WrittenQuestion;
  const hasFeedback = writtenFeedback && Object.keys(writtenFeedback).length > 0;

  return (
    <div className="quiz-view">
      <div className="quiz-header">
        <Link to={`/course/${courseId}`} className="back-link">
          <ArrowLeft size={18} />
          Zurück
        </Link>
        <span className="quiz-mode-badge">
          {quizMode === 'pflicht' ? '📋 Pflicht' : '🎯 Alle'}
        </span>
      </div>

      <div className="quiz-progress">
        Frage {currentIndex + 1} von {questions.length}
        <span style={{
          background: 'rgba(171, 71, 188, 0.2)',
          color: '#ce93d8',
          padding: '4px 12px',
          borderRadius: '20px',
          fontSize: '13px',
          fontWeight: 700,
          marginLeft: '8px',
        }}>
          ✍️ Schriftlich
        </span>
      </div>

      <div className="quiz-question">
        <h2>{wQuestion.question}</h2>
        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginTop: '8px' }}>
          ({wQuestion.maxPoints} Punkte möglich)
        </p>
      </div>

      {!writtenFeedback ? (
        <>
          <textarea
            value={writtenAnswer}
            onChange={(e) => setWrittenAnswer(e.target.value)}
            placeholder="Schreibe deine Antwort hier..."
            disabled={isEvaluating}
            style={{
              width: '100%',
              minHeight: '120px',
              background: 'rgba(255,255,255,0.08)',
              border: '2px solid rgba(255,255,255,0.15)',
              borderRadius: '12px',
              color: '#e8eaf6',
              fontSize: '16px',
              padding: '16px',
              fontFamily: 'Nunito, sans-serif',
              resize: 'vertical',
              boxSizing: 'border-box',
              marginTop: '16px',
            }}
          />
          <div className="quiz-actions">
            <button
              className="btn-primary"
              onClick={submitWrittenAnswer}
              disabled={!writtenAnswer.trim() || isEvaluating}
              style={{
                background: writtenAnswer.trim() && !isEvaluating ? '#ffd54f' : 'rgba(255,255,255,0.1)',
                color: writtenAnswer.trim() && !isEvaluating ? '#1a1a2e' : 'rgba(255,255,255,0.3)',
              }}
            >
              {isEvaluating ? '🤖 KI bewertet...' : 'Abschicken'}
            </button>
          </div>
        </>
      ) : (
        <>
          {/* Show user's answer */}
          <div style={{
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '12px',
            padding: '16px',
            marginTop: '16px',
            marginBottom: '16px',
            color: 'rgba(255,255,255,0.7)',
            fontSize: '15px',
          }}>
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', marginBottom: '8px' }}>
              Deine Antwort:
            </div>
            {writtenAnswer}
          </div>

          <p style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', marginBottom: '12px' }}>
            KI-Feedback
          </p>

          {/* PUNKTE */}
          {writtenFeedback.PUNKTE && (
            <div style={{
              background: 'rgba(255, 213, 79, 0.15)',
              borderLeft: '4px solid #ffd54f',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '12px',
            }}>
              <div style={{ color: '#ffd54f', fontWeight: 700, fontSize: '14px', marginBottom: '8px' }}>
                ⭐ PUNKTE
              </div>
              <div style={{ color: '#e8eaf6', fontSize: '15px', lineHeight: '1.5' }}>
                {writtenFeedback.PUNKTE}
              </div>
            </div>
          )}

          {/* RICHTIG */}
          {writtenFeedback.RICHTIG && (
            <div style={{
              background: 'rgba(76, 175, 80, 0.15)',
              borderLeft: '4px solid #4caf50',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '12px',
            }}>
              <div style={{ color: '#4caf50', fontWeight: 700, fontSize: '14px', marginBottom: '8px' }}>
                ✅ RICHTIG
              </div>
              <div style={{ color: '#e8eaf6', fontSize: '15px', lineHeight: '1.5' }}>
                {writtenFeedback.RICHTIG}
              </div>
            </div>
          )}

          {/* FEHLT */}
          {writtenFeedback.FEHLT && (
            <div style={{
              background: 'rgba(255, 152, 0, 0.15)',
              borderLeft: '4px solid #ff9800',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '12px',
            }}>
              <div style={{ color: '#ff9800', fontWeight: 700, fontSize: '14px', marginBottom: '8px' }}>
                📝 FEHLT
              </div>
              <div style={{ color: '#e8eaf6', fontSize: '15px', lineHeight: '1.5' }}>
                {writtenFeedback.FEHLT}
              </div>
            </div>
          )}

          {/* AUSDRUCK */}
          {writtenFeedback.AUSDRUCK && (
            <div style={{
              background: 'rgba(66, 165, 245, 0.15)',
              borderLeft: '4px solid #42a5f5',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '12px',
            }}>
              <div style={{ color: '#42a5f5', fontWeight: 700, fontSize: '14px', marginBottom: '8px' }}>
                💡 AUSDRUCK
              </div>
              <div style={{ color: '#e8eaf6', fontSize: '15px', lineHeight: '1.5' }}>
                {writtenFeedback.AUSDRUCK}
              </div>
            </div>
          )}

          {/* FAZIT */}
          {writtenFeedback.GESAMT && (
            <div style={{
              background: 'rgba(171, 71, 188, 0.15)',
              borderLeft: '4px solid #ab47bc',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '12px',
            }}>
              <div style={{ color: '#ab47bc', fontWeight: 700, fontSize: '14px', marginBottom: '8px' }}>
                🎯 FAZIT
              </div>
              <div style={{ color: '#e8eaf6', fontSize: '15px', lineHeight: '1.5' }}>
                {writtenFeedback.GESAMT}
              </div>
            </div>
          )}

          {/* Fallback: raw response if parsing failed */}
          {!hasFeedback && writtenRawResponse && (
            <div style={{
              background: 'rgba(255,255,255,0.08)',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '12px',
            }}>
              <div style={{ color: '#e8eaf6', fontSize: '15px', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                {writtenRawResponse}
              </div>
            </div>
          )}

          {/* Perfekte Antwort zum Lernen */}
          <div style={{
            background: 'rgba(76, 175, 80, 0.1)',
            border: '2px solid rgba(76, 175, 80, 0.3)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '12px',
            marginTop: '16px',
          }}>
            <div style={{
              color: '#4caf50',
              fontWeight: 700,
              fontSize: '14px',
              marginBottom: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              📖 Perfekte Antwort zum Lernen:
            </div>
            <div style={{
              color: '#e8eaf6',
              fontSize: '15px',
              lineHeight: '1.6',
              whiteSpace: 'pre-wrap',
            }}>
              {wQuestion.expectedAnswer}
            </div>
          </div>

          <div className="quiz-actions">
            {/* Lernhilfe Button - nur bei weniger als max Punkten */}
            {lastWrittenNeedsHelp && categoryLernhilfe[wQuestion.category] && (
              <button
                onClick={() => {
                  setLernhilfeTab('text');
                  setShowLernhilfe(true);
                }}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: 'rgba(255, 152, 0, 0.15)',
                  border: '2px solid rgba(255, 152, 0, 0.4)',
                  borderRadius: '12px',
                  color: '#ff9800',
                  fontSize: '15px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                💡 Nachlernen: {categoryLernhilfe[wQuestion.category].title}
              </button>
            )}

            {/* KI-Nachfrage Bereich */}
            <div style={{
              background: 'rgba(66, 165, 245, 0.08)',
              border: '1px solid rgba(66, 165, 245, 0.2)',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '12px',
            }}>
              <div style={{
                color: '#42a5f5',
                fontSize: '14px',
                fontWeight: 700,
                marginBottom: '12px'
              }}>
                🤖 Hast du noch eine Frage dazu?
              </div>

              {/* Bisherige Nachfragen anzeigen */}
              {followUpAnswers.map((qa, index) => (
                <div key={index} style={{ marginBottom: '12px' }}>
                  <div style={{
                    background: 'rgba(255, 213, 79, 0.1)',
                    borderRadius: '10px',
                    padding: '10px 14px',
                    marginBottom: '6px',
                    fontSize: '14px',
                    color: '#ffd54f',
                  }}>
                    💬 {qa.question}
                  </div>
                  <div style={{
                    background: 'rgba(66, 165, 245, 0.1)',
                    borderRadius: '10px',
                    padding: '10px 14px',
                    fontSize: '14px',
                    color: '#e8eaf6',
                    lineHeight: '1.5',
                  }}>
                    🤖 {qa.answer}
                  </div>
                </div>
              ))}

              {/* Eingabefeld */}
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={followUpQuestion}
                  onChange={(e) => setFollowUpQuestion(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && followUpQuestion.trim()) {
                      askFollowUp();
                    }
                  }}
                  placeholder="z.B. Was ist der Unterschied zwischen..."
                  style={{
                    flex: 1,
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: '10px',
                    color: '#e8eaf6',
                    fontSize: '14px',
                    padding: '12px',
                    fontFamily: 'Nunito, sans-serif',
                  }}
                />
                <button
                  onClick={askFollowUp}
                  disabled={!followUpQuestion.trim() || isAskingFollowUp}
                  style={{
                    background: followUpQuestion.trim() && !isAskingFollowUp ? '#42a5f5' : 'rgba(255,255,255,0.1)',
                    border: 'none',
                    borderRadius: '10px',
                    color: followUpQuestion.trim() && !isAskingFollowUp ? '#fff' : 'rgba(255,255,255,0.3)',
                    padding: '12px 16px',
                    fontSize: '14px',
                    fontWeight: 700,
                    cursor: followUpQuestion.trim() && !isAskingFollowUp ? 'pointer' : 'default',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {isAskingFollowUp ? '...' : '🤖'}
                </button>
              </div>
            </div>

            <button className="btn-primary" onClick={handleNext}>
              {currentIndex < questions.length - 1 ? 'Weiter' : 'Ergebnis'}
            </button>
          </div>

          {/* Lernhilfe Modal for written questions */}
          {showLernhilfe && categoryLernhilfe[wQuestion.category] && (
            <LernhilfeModal
              lernhilfe={categoryLernhilfe[wQuestion.category]}
              lernhilfeTab={lernhilfeTab}
              setLernhilfeTab={setLernhilfeTab}
              lernhilfeAudioPlaying={lernhilfeAudioPlaying}
              setLernhilfeAudioPlaying={setLernhilfeAudioPlaying}
              currentAudioIndex={currentAudioIndex}
              setCurrentAudioIndex={setCurrentAudioIndex}
              lernhilfeAudioRef={lernhilfeAudioRef}
              onClose={() => {
                setShowLernhilfe(false);
                if (lernhilfeAudioRef.current) {
                  lernhilfeAudioRef.current.pause();
                  setLernhilfeAudioPlaying(false);
                }
              }}
              flashcards={flashcards}
              mindmaps={mindmaps}
            />
          )}
        </>
      )}
    </div>
  );
}
